import { Link, useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { COMPANY, SERVICES, SERVICE_AREAS, PUBLIC_EMAIL } from "@/lib/constants";
import { useRef, useCallback } from "react";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import ChamberBadge from "@/components/shared/ChamberBadge";
import PreferredSourceCTA from "@/components/seo/PreferredSourceCTA";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const clickTimestamps = useRef<number[]>([]);
  const { trackEmailClick, trackPhoneClick } = useVisitorTracking();

  const handleSecretTap = useCallback(() => {
    const now = Date.now();
    clickTimestamps.current = clickTimestamps.current.filter(t => now - t < 3000);
    clickTimestamps.current.push(now);
    if (clickTimestamps.current.length >= 5) {
      clickTimestamps.current = [];
      navigate("/admin/login");
    }
  }, [navigate]);

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div>
            <img 
              src="/images/logo-112.webp"
              alt="O'Brien Contracting"
              className="h-14 w-14 mb-4"
              width={56}
              height={56}
              style={{ aspectRatio: "1 / 1" }}
              loading="lazy"
              decoding="async"
            />
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-6">
              O'Brien Contracting is a licensed roofing contractor based in North Port, FL. Serving North Port, Venice, Englewood, Port Charlotte, Sarasota, and Bradenton.
            </p>
            <div className="space-y-3">
              <a 
                href={COMPANY.phoneHref}
                className="flex items-center gap-3 text-sm hover:text-accent transition-colors"
                onClick={() => trackPhoneClick("footer")}
              >
                <Phone className="h-4 w-4 text-accent" />
                {COMPANY.phoneFormatted}
              </a>
              {PUBLIC_EMAIL && (
                <a
                  href={`mailto:${PUBLIC_EMAIL}`}
                  className="flex items-center gap-3 text-sm hover:text-accent transition-colors"
                  onClick={() => trackEmailClick("footer")}
                >
                  <Mail className="h-4 w-4 text-accent" />
                  {PUBLIC_EMAIL}
                </a>
              )}
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="h-4 w-4 text-accent mt-0.5" />
                <span>{COMPANY.address.full}</span>
              </div>
              <div className="text-sm text-primary-foreground/70">
                Licensed Roofing Contractor<br />
                License {COMPANY.license}<br />
                Serving North Port, Venice, Englewood, Port Charlotte, Sarasota, and Bradenton
              </div>
            </div>

            <div className="mt-8">
              <h5 className="font-heading font-bold text-lg mb-4">Follow Us</h5>
              <div className="flex flex-wrap gap-4">
                {[
                  {
                    label: "Facebook",
                    href: COMPANY.social.facebook,
                    brand: "#1877F2",
                    icon: <Facebook className="h-8 w-8" strokeWidth={2.25} />,
                  },
                  {
                    label: "Instagram",
                    href: COMPANY.social.instagram,
                    brand: "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF,#515BD4)",
                    icon: <Instagram className="h-8 w-8" strokeWidth={2.25} />,
                  },
                  {
                    label: "TikTok",
                    href: COMPANY.social.tiktok,
                    brand: "#010101",
                    icon: (
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M16.5 3a5.6 5.6 0 0 0 4.5 4.4v3a8.5 8.5 0 0 1-4.3-1.3v6.1a6.2 6.2 0 1 1-5.4-6.1v3.1a3.1 3.1 0 1 0 2.3 3V3h2.9z" />
                      </svg>
                    ),
                  },
                  {
                    label: "Yelp",
                    href: COMPANY.social.yelp,
                    brand: "#D32323",
                    icon: (
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M11.05 3.02c.9-.13 2.23-.19 2.85.62.35.46.36 1.1.39 1.66.04.67-.03 1.34-.07 2.01-.09 1.77-.2 3.54-.3 5.31-.03.53-.03 1.18-.38 1.61a1.2 1.2 0 0 1-.65.42c-.23.06-.48.04-.7-.05a1.06 1.06 0 0 1-.11-.04c-.5-.24-.78-.82-1.04-1.29a319 319 0 0 1-2.6-4.64c-.32-.59-.67-1.15-.93-1.78-.22-.51-.5-1.09-.38-1.66.21-1 1.43-1.52 2.3-1.8.27-.09.55-.16.83-.22.26-.06.53-.11.79-.15zM3.98 9.96c.24 0 .52.05.92.17.06.02.12.04.19.06.33.1.7.24 1.15.4.68.24 1.36.49 2.04.74l1.21.44c.22.08.44.18.64.3.18.1.33.23.45.39.09.13.16.28.2.43a1.22 1.22 0 0 1-.88 1.45l-.1.03-4.78 1.1c-.38.09-.76.19-1.15.16-.26-.02-.51-.04-.76-.21a1.18 1.18 0 0 1-.33-.36c-.35-.55-.37-1.37-.37-2 0-.71.1-1.41.33-2.07.03-.09.07-.17.11-.25.04-.07.08-.15.13-.21a2.5 2.5 0 0 1 .15-.2c.11-.13.25-.22.4-.28.14-.06.3-.09.45-.09zm14.85-2.4c.08.05.15.11.21.18.35.4.66.83.92 1.29.28.57.62 1.31.54 1.96a1.18 1.18 0 0 1-.14.47c-.15.26-.36.39-.59.53-.34.19-.72.27-1.1.36l-4.78 1.1a1.22 1.22 0 0 1-1.53-.9l-.01-.02a1.22 1.22 0 0 1 .23-1c.13-.2.28-.38.45-.55l.9-.93c.5-.52 1.01-1.04 1.51-1.56.34-.34.61-.63.86-.86.05-.05.1-.09.15-.13.3-.28.53-.45.74-.56.14-.07.29-.11.45-.12.16-.02.33 0 .48.08.08.03.16.07.23.11.07.05.14.09.2.14l.28.41zm-7.78 8.06c.16.09.3.24.4.4.09.14.16.28.18.41.08.3.09.63.08.94l-.05 1.3c-.02.74-.05 1.47-.09 2.2-.02.48-.04.89-.07 1.23 0 .07-.01.14-.02.2-.04.41-.1.7-.19.92-.05.15-.14.29-.25.4-.12.11-.26.2-.42.26-.08.03-.16.05-.24.07-.08.02-.17.03-.25.04a1.6 1.6 0 0 1-.27 0 6.1 6.1 0 0 1-1.57-.3 6.3 6.3 0 0 1-1.72-.84 1.84 1.84 0 0 1-.39-.35 2.3 2.3 0 0 1-.15-.21 1.1 1.1 0 0 1-.16-.47c-.02-.16 0-.32.05-.47.06-.23.19-.49.42-.84l.11-.16c.19-.29.43-.62.71-1.01l1.29-1.79.77-1.06c.05-.04.08-.1.12-.15.15-.2.32-.39.52-.53.07-.05.14-.1.22-.14.05-.02.1-.05.15-.06a1.1 1.1 0 0 1 .82.01zm3.06.02c.06 0 .11.01.17.02.24.05.47.14.7.25.05.03.11.07.17.09l1.15.61c.65.34 1.3.69 1.94 1.04.42.23.78.42 1.08.6.06.03.12.07.17.1.36.22.59.39.75.57.11.12.19.26.25.4.05.16.07.32.05.49 0 .09-.02.17-.04.25-.02.08-.05.16-.08.24-.03.09-.07.17-.11.25a6.4 6.4 0 0 1-1.18 1.51 6.1 6.1 0 0 1-1.28.96 1.86 1.86 0 0 1-.25.11c-.08.03-.16.06-.24.08a2.4 2.4 0 0 1-.25.04 1.15 1.15 0 0 1-.49-.05 1.09 1.09 0 0 1-.4-.25c-.18-.16-.35-.39-.57-.75l-.1-.17c-.18-.3-.37-.66-.6-1.08l-1.04-1.94-.61-1.15c-.14-.27-.28-.57-.34-.88a1.42 1.42 0 0 1-.01-.44c.03-.2.12-.4.25-.55.14-.17.33-.29.54-.35a1.1 1.1 0 0 1 .37-.05z" />
                      </svg>
                    ),
                  },
                ].map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ background: item.brand }}
                    className="w-16 h-16 shrink-0 rounded-full text-white ring-2 ring-white/25 flex items-center justify-center shadow-md hover:-translate-y-1 hover:shadow-xl transition-all"
                    aria-label={item.label}
                  >
                    {item.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Our Services</h4>
            <ul className="space-y-2">
              {SERVICES.slice(0, 8).map((service) => (
                <li key={service.slug}>
                  <Link
                    to={service.slug === "roof-repair" || service.slug === "roof-replacement" ? `/${service.slug}` : `/${service.slug}`}
                    className="text-sm text-primary-foreground/70 hover:text-accent transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Service Areas</h4>
            <ul className="space-y-2">
              {SERVICE_AREAS.map((area) => (
                <li key={area.slug}>
                  <Link
                    to={`/roofing/${area.slug}`}
                    className="text-sm text-primary-foreground/70 hover:text-accent transition-colors"
                  >
                    {area.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/roofing-contractor-north-port-fl" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Roofing Contractor North Port FL
                </Link>
              </li>
              <li>
                <Link to="/roof-replacement-venice-fl" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Roof Replacement Venice FL
                </Link>
              </li>
              <li>
                <Link to="/roof-replacement-sarasota-fl" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Roof Replacement in Sarasota, FL
                </Link>
              </li>
              <li>
                <Link to="/metal-roofing-naples-fl" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Metal Roofing Naples FL
                </Link>
              </li>
              <li>
                <Link
                  to="/neighborhoods"
                  className="text-sm font-semibold text-accent hover:underline"
                >
                  Browse All Neighborhoods →
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/quick-estimate" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Quick Estimate
                </Link>
              </li>
              <li>
                <Link to="/before-after" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Before/After Gallery
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Customer Reviews
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Roofing Blog
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/roofing-costs-by-city" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Roofing Costs by City
                </Link>
              </li>
              <li>
                <Link to="/roofing-near-me" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Roofing Near Me
                </Link>
              </li>
              <li>
                <Link to="/compare-materials" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                  Compare Materials
                </Link>
              </li>
            </ul>

            {/* Spanish Pages */}
            <div className="mt-4">
              <h5 className="font-semibold text-sm mb-2">En Español</h5>
              <ul className="space-y-1">
                <li>
                  <Link to="/es" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                    Página Principal
                  </Link>
                </li>
                <li>
                  <Link to="/es/servicios" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                    Servicios
                  </Link>
                </li>
                <li>
                  <Link to="/es/presupuesto-gratis" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                    Presupuesto Gratis
                  </Link>
                </li>
                <li>
                  <Link to="/es/contacto" className="text-sm text-primary-foreground/70 hover:text-accent transition-colors">
                    Contacto
                  </Link>
                </li>
              </ul>
            </div>

          </div>

        </div>

        {/* Trust Badges & Memberships */}
        <div className="mt-10 pt-8 border-t border-primary-foreground/10">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="shrink-0">
              <p className="text-xs uppercase tracking-widest text-primary-foreground/60 mb-3 text-center lg:text-left">
                Trusted &amp; Verified
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start items-stretch gap-4">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  title="BuildZoom.com"
                  href="https://www.buildzoom.com/contractor/obrien-contracting-llc?ad_location=co_website"
                  className="bg-white rounded-xl p-4 w-52 h-28 flex items-center justify-center shadow-md hover:-translate-y-0.5 hover:shadow-xl transition-all"
                >
                  <img
                    alt="Featured on BuildZoom"
                    className="max-h-full max-w-full object-contain"
                    src="https://badges.buildzoom.com/2023/fon1.png"
                    loading="lazy"
                  />
                </a>

                <div className="bg-white rounded-xl p-4 w-52 h-28 flex items-center justify-center shadow-md">
                  <ChamberBadge />
                </div>
              </div>
            </div>

            {/* Google Preferred Source opt-in */}
            <PreferredSourceCTA
              theme="dark"
              className="flex-1 w-full"
              headline="Get our Florida roofing guides first in Google Search and AI answers."
            />
          </div>

          {/* Hidden BuildZoom tracking pixel */}
          <img
            src="https://track.buildzoom.com//badge_load?track_id=lYQ35R&entity=Contractor&event_type=impression&ad_type=contractor_badge"
            width={1}
            height={1}
            alt=""
            className="hidden"
          />
        </div>
      </div>







      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div
              className="text-sm text-primary-foreground/60 cursor-default select-none"
              onClick={handleSecretTap}
            >
              © {currentYear} {COMPANY.name}. All rights reserved. | License #{COMPANY.license}
            </div>
            <div className="flex items-center gap-4 text-sm text-primary-foreground/60">
              <Link to="/privacy" className="hover:text-accent transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-accent transition-colors">Terms of Service</Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("obc:open-consent-preferences"))}
                className="hover:text-accent transition-colors underline-offset-2 hover:underline"
                aria-label="Open Cookie Preferences"
              >
                Cookie Preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
